import _, { get } from "lodash"
import { useState, useContext, useEffect, useMemo } from "react"
import { useLazyQuery, useQuery } from "@apollo/client"
import { Formik, Form as FormWrapper } from "formik"
import { Button } from "primereact/button"
import { classNames } from "primereact/utils"
import { v4 as uuidv4 } from "uuid"

// Context
import { UserContext } from "../../../context/userContext"

// Queries
import { GET_ENTERPRISE_FIELD_DEFS, PARTICIPANT_BLOCK } from "./queries"

// Helpers
import validationSchema from "../../../helpers/validationSchema/"
import initialValues from "../../../helpers/initialValues"
import {
  buildFieldDefMap,
  normalizePayload,
  transformPayload,
} from "../../../helpers/dateNormalization"
import { cleanMultiFieldValues } from "../../../helpers/cleanMultiFieldValues"

// Comps
import FieldBuilder from "./FieldBuilder"
import { LoaderMedium } from "../../../components/Loaders"
import ParticipantProfileBlocks from "../../FormViewMode/ParticipantProfileBlocks"

// Helpers
import GLOBALS_STATUS from "../../../config/globalsStatus"
import { cleanupTmpFields } from "../../../components/FieldTypes/helpers/cleanupTempFields"
import sanitizeTemplateBlocks from "../../../helpers/sanitizeTemplateBlocks"
import { callCloudFunction } from "../../../helpers/cloudFunctionRequests"

import styles from "../../../styles/Participant-Profile.module.css"
import formStyles from "../../../styles/Form.module.css"
import { LogError } from "../../../helpers/logger"
import ErrorMessage from "./components/ErrorMessage"
import { Dialog } from "primereact/dialog"

const ALLOWED_EDIT_STATUSES = [
  ...GLOBALS_STATUS.ACTIVE_STATUS,
  GLOBALS_STATUS.KIOSK_MEMBER,
]

export default function Form(props) {
  const [editMode, setEditMode] = useState(false)
  const [fetchData, setFetchData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState(false)
  const [showError, setShowError] = useState(false)
  const [showErrorMessage, setShowErrorMessage] = useState("")

  const [formId] = useState(() => uuidv4())

  const userCtx = useContext(UserContext)
  const participant_id = props.participantId

  const [getBlockData] = useLazyQuery(PARTICIPANT_BLOCK, {
    fetchPolicy: "network-only",
    variables: {
      type: "auto",
      organization_id: userCtx.session.user.organization_id,
      enterprise_id: userCtx.session.user.enterprise_id,
      participant_id,
      idBlock: props.blockName,
    },
    onCompleted: (data) => {
      // Sanitize blocks to remove any password fields that shouldn't be rendered
      const sanitizedBlocks = sanitizeTemplateBlocks(props.blockName, [
        data.block,
      ])
      const sanitizedData = { ...data, block: sanitizedBlocks[0] }

      setFetchData(sanitizedData)
      setLoading(false)
    },
    onError: (error) => {
      LogError("Error fetching block data:", error)
      setErrorMessage(
        get(
          error,
          "response.data.message",
          "An error occurred while fetching the block data.",
        ),
      )
      setLoading(false)
    },
  })

  const { data: enterpriseFieldDefsData } = useQuery(GET_ENTERPRISE_FIELD_DEFS, {
    variables: {
      enterprise_id: userCtx.session.user.enterprise_id,
    },
    skip: !userCtx.session.user.enterprise_id,
    fetchPolicy: "cache-first",
  })

  const dateFieldDefMap = useMemo(() => {
    return buildFieldDefMap(
      enterpriseFieldDefsData?.fieldDefs || [],
      "ParticipantProfileForm",
    )
  }, [enterpriseFieldDefsData?.fieldDefs])

  // On load, fetch the block data
  useEffect(() => {
    getBlockData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dataStored = useMemo(() => {
    if (!fetchData?.blockSaved?.data) return {}

    try {
      return JSON.parse(fetchData.blockSaved.data) || {}
    } catch (error) {
      LogError("Error parsing blockSaved data:", error)
      return {}
    }
  }, [fetchData])

  const formValues = useMemo(() => {
    if (!fetchData?.block) return {} // Wait for fetchData and block to be available before computing form values
    const values = {
      ...initialValues(fetchData?.block),
      ...transformPayload(dataStored?.values, dateFieldDefMap),
    }
    // Remove any multi-field blocks that are completely empty
    cleanMultiFieldValues(values, fetchData?.block?.fields)
    return values
  }, [fetchData, dataStored, dateFieldDefMap])

  if (errorMessage && !fetchData?.block) {
    return (
      <div className={styles.tabPageHeader}>
        <span style={{ color: "red" }}>{errorMessage}</span>
      </div>
    )
  }
  if (loading || !fetchData?.block) return <LoaderMedium />

  const handleSubmit = async (values, actions) => {
    setLoading(true)
    setErrorMessage(null)

    let savedSuccessfully = false
    try {
      // Submit each block
      const postBody = {
        ...(dataStored?.idDoc ? { idDoc: dataStored.idDoc } : {}),
        organization_alias: userCtx.session.user.organization_alias,
        organization_id: userCtx.session.user.organization_id,
        enterprise_id: userCtx.session.user.enterprise_id,
        enterprise_alias: userCtx.session.user.enterprise_alias,
        participant_alias: userCtx.participant_alias || "not-set",
        participant_membership_id:
          userCtx.participant_membership_id || "not-set",
        participant_id,
        staff_alias: userCtx.session.user.staff_alias,
        staff_id: userCtx.session.user.staff_id,
        block_name: props.blockName,
        form_id: formId,
        values: cleanupTmpFields(values),
      }
      
      const normalizedBody = normalizePayload(postBody, dateFieldDefMap)

      await callCloudFunction(
        "blockSubmit",
        normalizedBody,
        userCtx.tokens.getMainToken(),
      )

      try {
        await callCloudFunction(
          "deleteStagedFormSubmission",
          { form_id: formId },
          userCtx.tokens.getMainToken(),
        )
      } catch (cleanupError) {
        LogError(
          `Failed to clean up staged submission (form_id: ${formId}):`,
          cleanupError,
        )
      }

      savedSuccessfully = true
    } catch (error) {
      savedSuccessfully = false
      setShowError(true)
      setShowErrorMessage(
        error?.message ||
          (typeof error === "string" ? error : "An unknown error occurred"),
      )
      setErrorMessage(
        get(
          error,
          "response.data.message",
          "An error occurred while submitting the form.",
        ),
      )
      setLoading(false)
    }

    if (savedSuccessfully) {
      // After successful submission, update participant context and refetch data
      userCtx.changeParticipant(participant_id)
      userCtx.triggerProfileBoxRefresh() // Trigger a refresh to the participant profile box after successful form submission
      getBlockData()

      // Reset form state and exit edit mode
      setEditMode(!editMode)
    }

    return savedSuccessfully
  }

  const EditButtonToggle = () => {
    return _.includes(
      ALLOWED_EDIT_STATUSES,
      userCtx.participant_membership_status,
    ) ? (
      <Button
        label={editMode ? "View" : "Edit"}
        className={classNames("p-button-sm", styles.editButtonToggle)}
        onClick={() => setEditMode(!editMode)}
      />
    ) : null
  }

  if (!editMode) {
    return (
      <>
        <div className={styles.tabPageHeader}>
          <h1 className="profile-title">
            {_.startCase(props.blockName.replace("__", " ").replace("_", " "))}
          </h1>
          <EditButtonToggle />
        </div>

        <ParticipantProfileBlocks
          idBlock={props.blockName}
          className="profile-tabs-view"
        />
      </>
    )
  }

  return (
    <>
      {showError && (
        <Dialog
          header="Error"
          visible={true}
          onHide={() => setShowError(false)}
        >
          <ErrorMessage
            formId={formId}
            message={showErrorMessage}
            onCancel={() => {
              setShowError(false)
            }}
          />
        </Dialog>
      )}
      <div className={styles.tabPageHeader}>
        <h1 className="profile-title">
          {_.startCase(props.blockName.replace("__", " ").replace("_", " "))}
        </h1>
        <EditButtonToggle />
      </div>

      <div className={styles.customSpacedHr} />

      <Formik
        key={props.blockName}
        initialValues={formValues}
        validationSchema={validationSchema([fetchData.block], "block", true)}
        onSubmit={async (values, actions) => {
          const saveRes = await handleSubmit(values, actions)
          if (saveRes) {
            props.triggerParticipantQuery()
            setFetchData(null)
          }
        }}
      >
        <FormWrapper className={formStyles.form} key={props.blockName}>
          <FieldBuilder fields={fetchData.block.fields} />
          {userCtx.participant_membership_status !== "Inactive Discharged" && (
            <button
              type="submit"
              className={classNames("btn", formStyles.btnSubmit)}
              disabled={loading}
            >
              Submit
            </button>
          )}
          {errorMessage && (
            <span
              style={{ marginLeft: "20px", color: "red" }}
              className="error"
            >
              {errorMessage}
            </span>
          )}
        </FormWrapper>
      </Formik>
    </>
  )
}
